import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import Swal from "sweetalert2";
import axiosInstance from "../../api/axiosInstance";

// On-the-spot consent creation, triggered when Force Consents blocks adding a
// service. Mirrors the "Add Forms → Consent" modal in PatientForms.jsx so
// staff see the same fields/layout, but signatures stay optional — Force
// Consents only requires a dated record, not a signed one. Steps through
// each missing template one at a time.
const QuickAddConsentModal = ({ open, onClose, patientId, patient, missing, onDone }) => {
  const [index, setIndex] = useState(0);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const consentEditorRef = useRef(null);
  const consentEditorInstanceRef = useRef(null);
  const patientSigRef = useRef(null);
  const userSigRef = useRef(null);

  const getDoctorName = () => {
    try {
      const userMedical = localStorage.getItem("userMedical");
      if (userMedical) {
        const parsed = JSON.parse(userMedical);
        const data = parsed.data || parsed;
        return data.displayName || data.fullName || data.name || "";
      }
    } catch (_) { /* ignore */ }
    return "";
  };

  useEffect(() => {
    if (!open) return;
    setIndex(0);
  }, [open, missing]);

  useEffect(() => {
    if (!open || !missing || !missing[index]) return;
    const template = missing[index];
    setForm({
      templateId: template.id,
      templateName: template.name,
      title: template.name,
      content: template.content || "",
      patientName: patient ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim() : "",
      fileNo: patient?.fileNumber || patient?.id || "",
      cprNo: patient?.civilIdNumber || patient?.cpr || "",
      ageSex: `${patient?.age || ""}/${patient?.gender || ""}`,
      createdBy: getDoctorName(),
      createdAt: new Date().toLocaleString(),
      patientSignature: "",
      userSignature: "",
    });
  }, [open, index, missing, patient]);

  // Init CKEditor for the content field, matching PatientForms.jsx's consent editor
  useEffect(() => {
    if (!open || !form) return;
    const initEditor = () => {
      if (!consentEditorRef.current) return;
      if (consentEditorInstanceRef.current) {
        consentEditorInstanceRef.current.destroy().catch(() => { });
        consentEditorInstanceRef.current = null;
      }
      if (window.ClassicEditor) {
        window.ClassicEditor.create(consentEditorRef.current)
          .then(editor => {
            consentEditorInstanceRef.current = editor;
            editor.setData(form.content || "");
          })
          .catch(console.error);
      } else {
        const existing = document.getElementById("ckeditor5-cdn-consents");
        if (!existing) {
          const s = document.createElement("script");
          s.id = "ckeditor5-cdn-consents";
          s.src = "https://cdn.ckeditor.com/ckeditor5/41.4.2/classic/ckeditor.js";
          s.onload = initEditor;
          document.head.appendChild(s);
        } else {
          existing.addEventListener("load", initEditor, { once: true });
        }
      }
    };
    const timer = setTimeout(initEditor, 150);
    return () => {
      clearTimeout(timer);
      if (consentEditorInstanceRef.current) {
        consentEditorInstanceRef.current.destroy().catch(() => { });
        consentEditorInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index]);

  if (!open || !form) return null;

  const total = missing.length;
  const isLast = index === total - 1;

  const handleSave = async () => {
    setSaving(true);
    try {
      const content = consentEditorInstanceRef.current
        ? consentEditorInstanceRef.current.getData()
        : form.content;
      await axiosInstance.post("addPatientConsentForm", {
        patient_id: patientId,
        template_id: form.templateId,
        template_name: form.templateName,
        title: form.title,
        content,
        patient_name: form.patientName,
        file_no: form.fileNo,
        cpr_no: form.cprNo,
        age_sex: form.ageSex,
        created_by: form.createdBy,
        patient_signature: patientSigRef.current && !patientSigRef.current.isEmpty()
          ? patientSigRef.current.toDataURL("image/png")
          : "",
        user_signature: userSigRef.current && !userSigRef.current.isEmpty()
          ? userSigRef.current.toDataURL("image/png")
          : "",
      });
      if (isLast) {
        onDone?.(true);
      } else {
        setIndex(i => i + 1);
      }
    } catch (error) {
      console.error("Failed to save consent:", error);
      Swal.fire("Error", "Failed to save consent. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal fade show" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)", display: "block" }} aria-modal="true">
      <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: "860px" }}>
        <div className="modal-content">
          <div className="modal-header" style={{ background: "linear-gradient(135deg,#1a7e5a 0%,#2ca87f 100%)" }}>
            <h5 className="modal-title text-white d-flex align-items-center gap-2">
              <i className="ti ti-file-description f-18" />
              Add Consent — {form.templateName}
              {total > 1 ? ` (${index + 1} of ${total})` : ""}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} disabled={saving} />
          </div>
          <div style={{ background: "#f0faf6", borderBottom: "1px solid #d1f0e5", padding: "8px 20px" }}>
            <div className="d-flex flex-wrap gap-3" style={{ fontSize: 13 }}>
              <span><i className="ti ti-user text-success me-1" /><strong>Patient:</strong> {form.patientName || "—"}</span>
              <span><i className="ti ti-id text-success me-1" /><strong>File No.:</strong> {form.fileNo || "—"}</span>
              <span><i className="ti ti-address-book text-success me-1" /><strong>CPR No.:</strong> {form.cprNo || "—"}</span>
              <span><i className="ti ti-cake text-success me-1" /><strong>Age / Gender:</strong> {form.ageSex || "—"}</span>
              <span><i className="ti ti-stethoscope text-success me-1" /><strong>Prepared By:</strong> {form.createdBy || "—"}</span>
              <span><i className="ti ti-calendar text-success me-1" /><strong>Date:</strong> {form.createdAt || "—"}</span>
            </div>
          </div>
          <div className="modal-body py-2 px-3">
            <fieldset disabled={saving}>
              <div className="mb-2">
                <label className="form-label mb-0" style={{ fontSize: 11 }}><i className="ti ti-heading me-1 text-muted" />Consent Title</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>

              <div className="mb-2">
                <label className="form-label mb-0" style={{ fontSize: 11 }}><i className="ti ti-notes me-1 text-muted" />Content</label>
                <textarea ref={consentEditorRef} defaultValue={form.content} />
              </div>

              <div className="row g-2">
                <div className="col-md-6">
                  <label className="form-label mb-0" style={{ fontSize: 11 }}><i className="ti ti-signature me-1 text-muted" />Patient Signature <span className="text-muted">(optional)</span></label>
                  <div style={{ border: "1px solid #dee2e6", borderRadius: 6, background: "#fff", position: "relative" }}>
                    <SignatureCanvas
                      ref={patientSigRef}
                      penColor="#1a237e"
                      canvasProps={{ width: 380, height: 120, style: { borderRadius: 6, width: "100%", height: 120 } }}
                    />
                    <button type="button"
                      onClick={() => patientSigRef.current?.clear()}
                      style={{ position: "absolute", top: 4, right: 6, fontSize: 11, padding: "1px 8px", background: "#f5f5f5", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}>
                      Clear
                    </button>
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label mb-0" style={{ fontSize: 11 }}><i className="ti ti-signature me-1 text-muted" />Staff Signature <span className="text-muted">(optional)</span></label>
                  <div style={{ border: "1px solid #dee2e6", borderRadius: 6, background: "#fff", position: "relative" }}>
                    <SignatureCanvas
                      ref={userSigRef}
                      penColor="#1a237e"
                      canvasProps={{ width: 380, height: 120, style: { borderRadius: 6, width: "100%", height: 120 } }}
                    />
                    <button type="button"
                      onClick={() => userSigRef.current?.clear()}
                      style={{ position: "absolute", top: 4, right: 6, fontSize: 11, padding: "1px 8px", background: "#f5f5f5", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}>
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
          <div className="modal-footer d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-sm d-flex align-items-center gap-1"
              style={{ fontSize: 13, padding: "5px 16px", background: "#f1f3f5", color: "#495057", border: "1px solid #dee2e6", borderRadius: 6 }}
              onClick={onClose}
              disabled={saving}
            >
              <i className="ti ti-x" /> Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm d-flex align-items-center gap-1"
              style={{ fontSize: 13, padding: "5px 16px", background: "#2ca87f", color: "#fff", border: "1px solid #2ca87f", borderRadius: 6 }}
              onClick={handleSave}
              disabled={saving}
            >
              <i className="ti ti-device-floppy" /> {saving ? "Saving..." : isLast ? "Save & Continue" : "Save & Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickAddConsentModal;
